import React, { useEffect, useId, useRef, useState } from 'react';
import LocalOcrPanel from './LocalOcrPanel';
import LightOnPanel from './LightOnPanel';

const MAX_CHARACTERS = 20000;
const MAX_FILE_BYTES = 4 * 1024 * 1024;
const MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const SOURCES = ['manual', 'local-ocr', 'mistral-ocr', 'lighton-ocr'];

async function readJson(url, options) {
  const response = await fetch(url, options);
  let body;
  try { body = await response.json(); }
  catch { throw new Error('Le service OCR distant ne répond pas correctement.'); }
  if (!response.ok) throw new Error(body?.error || 'Le service OCR distant est indisponible.');
  return body;
}

function readBase64(file, signal) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    const abort = () => { reader.abort(); reject(new DOMException('Extraction annulée.', 'AbortError')); };
    const cleanup = () => signal.removeEventListener('abort', abort);
    reader.onload = () => {
      cleanup();
      const value = String(reader.result || '').split(',')[1];
      if (!value) reject(new Error('Le fichier est illisible.')); else resolve(value);
    };
    reader.onerror = () => { cleanup(); reject(new Error('Le fichier est illisible.')); };
    reader.onabort = () => { cleanup(); reject(new DOMException('Extraction annulée.', 'AbortError')); };
    if (signal.aborted) { abort(); return; }
    signal.addEventListener('abort', abort, { once: true });
    reader.readAsDataURL(file);
  });
}

/** Controlled copy text. The parent owns all persistence and subsequent AI use. */
export default function StudentCopyPanel({ record, namespaceKey = 'local', expectedOwnerId, onChange }) {
  const titleId = useId();
  const scopeKey = JSON.stringify([namespaceKey, record?.id, expectedOwnerId || '']);
  const [file, setFile] = useState(null), [consent, setConsent] = useState(false);
  const [metadata, setMetadata] = useState(null), [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true), [availabilityError, setAvailabilityError] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [status, setStatus] = useState('');
  const [refresh, setRefresh] = useState(0);
  const epoch = useRef(0), operation = useRef(null), fileInput = useRef(null);
  const latest = useRef(null);
  latest.current = { scopeKey, record, onChange };
  const text = typeof record?.studentCopy === 'string' ? record.studentCopy : '';
  const source = SOURCES.includes(record?.studentCopySource) ? record.studentCopySource : 'manual';
  const maxBytes = Math.min(MAX_FILE_BYTES, Number(metadata?.maxFileBytes || metadata?.maxBytes) || MAX_FILE_BYTES);
  const ownerMatches = !expectedOwnerId || session?.user?.id === expectedOwnerId;
  const ready = !loading && metadata?.enabled === true && metadata?.provider === 'mistral'
    && session?.authenticated === true && Boolean(session?.user?.id) && Boolean(session?.csrfToken) && ownerMatches;

  useEffect(() => {
    const controller = new AbortController();
    const scope = scopeKey;
    epoch.current += 1; operation.current?.abort(); operation.current = null;
    setFile(null); setConsent(false); setBusy(false); setError(''); setStatus('');
    setMetadata(null); setSession(null); setLoading(true); setAvailabilityError('');
    if (fileInput.current) fileInput.current.value = '';
    const active = () => !controller.signal.aborted && latest.current.scopeKey === scope;
    Promise.all([
      readJson('/api/ocr.php', { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' }),
      readJson('/api/account.php?action=status', { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' }),
    ]).then(([config, account]) => {
      if (!active()) return;
      if (typeof config?.enabled !== 'boolean') throw new Error('La configuration OCR distante est indisponible.');
      setMetadata(config); setSession(account);
    }).catch(e => {
      if (active() && e.name !== 'AbortError') setAvailabilityError(e.message || 'Le service OCR distant est indisponible.');
    }).finally(() => { if (active()) setLoading(false); });
    return () => { controller.abort(); epoch.current += 1; operation.current?.abort(); operation.current = null; };
  }, [scopeKey, refresh]);

  const cancel = (message = '') => {
    epoch.current += 1; operation.current?.abort(); operation.current = null;
    setBusy(false); setStatus(message);
  };
  const validateFile = value => {
    if (!value || !MIME_TYPES.includes(value.type)) throw new Error('Choisissez un PDF, une image JPEG ou PNG.');
    if (!value.size) throw new Error('Le fichier choisi est vide.');
    if (value.size > maxBytes) throw new Error(`Fichier trop volumineux : ${(maxBytes / 1024 / 1024).toFixed(0)} Mo maximum.`);
  };
  const choose = event => {
    cancel(); setError(''); setConsent(false);
    const next = event.target.files?.[0] || null;
    try { if (next) validateFile(next); setFile(next); }
    catch (e) { setFile(null); event.target.value = ''; setError(e.message); }
  };
  const updateText = (value, nextSource) => {
    if (typeof value !== 'string' || value.length > MAX_CHARACTERS) {
      setError('Le texte dépasse 20 000 caractères. Raccourcissez-le avant de l’utiliser ; aucun texte existant n’a été remplacé.');
      return;
    }
    cancel(); setError('');
    onChange({ studentCopy: value, studentCopySource: nextSource, studentCopyConfirmed: false });
  };
  const applyLocal = value => {
    if (latest.current.scopeKey !== scopeKey) return;
    if (typeof value !== 'string' || value.length > MAX_CHARACTERS) { updateText(value, 'local-ocr'); return; }
    if (text.trim() && !window.confirm('Remplacer le texte de la copie de cet élève par cette extraction locale ?')) return;
    updateText(value, 'local-ocr');
    setStatus('Texte local ajouté à la copie. Relisez-le puis confirmez le texte relu.');
  };
  const extract = async () => {
    if (!ready || !file || !consent || operation.current) return;
    try { validateFile(file); } catch (e) { setError(e.message); return; }
    if (text.trim() && !window.confirm('Remplacer le texte de la copie de cet élève par une nouvelle extraction Mistral ?')) return;
    const controller = new AbortController(), token = ++epoch.current, scope = scopeKey;
    const initialCopy = text, initialSource = record?.studentCopySource, initialConfirmation = record?.studentCopyConfirmed;
    const ownerId = expectedOwnerId || session.user.id;
    operation.current = controller; setBusy(true); setError(''); setStatus('Vérification de la session avant envoi…');
    const active = () => token === epoch.current && latest.current.scopeKey === scope && !controller.signal.aborted;
    try {
      const account = await readJson('/api/account.php?action=status', { signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
      if (!active()) return;
      if (!account.authenticated || !account.user?.id || !account.csrfToken) throw new Error('Connectez-vous à votre compte enseignant puis vérifiez la disponibilité avant l’envoi.');
      if (account.user.id !== ownerId) throw new Error('La session a changé. Rouvrez le bon compte avant de transmettre cette copie.');
      const documentBase64 = await readBase64(file, controller.signal);
      if (!active()) return;
      setStatus('Extraction Mistral en cours. Le manuscrit peut contenir des erreurs de reconnaissance…');
      const result = await readJson('/api/ocr.php', {
        signal: controller.signal, method: 'POST', credentials: 'same-origin', cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': account.csrfToken },
        body: JSON.stringify({ documentBase64, mimeType: file.type }),
      });
      if (!active()) return;
      if (typeof result.text !== 'string' || !result.text.trim() || result.text.length > MAX_CHARACTERS
        || !Number.isInteger(result.pageCount) || result.pageCount < 1 || result.pageCount > 5) {
        throw new Error('Le texte OCR reçu est vide, invalide ou dépasse 20 000 caractères. Votre texte existant est conservé.');
      }
      const current = latest.current.record;
      if ((current?.studentCopy || '') !== initialCopy || current?.studentCopySource !== initialSource || current?.studentCopyConfirmed !== initialConfirmation) {
        throw new Error('La copie a changé pendant l’extraction. Vos modifications sont conservées ; relancez l’extraction si nécessaire.');
      }
      latest.current.onChange({ studentCopy: result.text, studentCopySource: 'mistral-ocr', studentCopyConfirmed: false });
      setConsent(false); setFile(null); if (fileInput.current) fileInput.current.value = '';
      setStatus(`${result.pageCount} page${result.pageCount > 1 ? 's' : ''} extraite${result.pageCount > 1 ? 's' : ''}. Relisez et corrigez le texte avant de le confirmer.`);
    } catch (e) {
      if (active() && e.name !== 'AbortError') { setError(e.message || 'Extraction Mistral impossible.'); setStatus(''); }
    } finally {
      if (active()) { operation.current = null; setBusy(false); }
    }
  };
  if (!record) return null;
  return <section className="studio-card studio-import-zone" aria-labelledby={titleId}>
    <h2 id={titleId}>Copie de l’élève</h2>
    <p>Ajoutez le travail de l’élève ici, séparément de vos observations et de votre dictée. L’OCR aide à lire une copie, mais reste imparfait, notamment pour le manuscrit : comparez toujours le texte avec l’original.</p>
    <details><summary>Extraire localement un PDF ou une image</summary>
      <LocalOcrPanel key={scopeKey} namespaceKey={scopeKey} onText={applyLocal} />
    </details>
    <details><summary>Extraire sur le serveur Copiveo avec LightOn, après votre accord</summary>
      <LightOnPanel key={'lighton-'+scopeKey} namespaceKey={scopeKey} expectedOwnerId={expectedOwnerId} onText={value=>{
        if(latest.current.scopeKey!==scopeKey)return;
        if(text.trim()&&!window.confirm('Remplacer le texte de cette copie par le résultat LightOn relu ?'))return;
        updateText(value,'lighton-ocr');
        setStatus('Texte LightOn ajouté. Relisez-le puis confirmez le texte relu.');
      }}/>
    </details>
    <details><summary>Extraire avec Mistral OCR, après votre accord</summary>
      <p>PDF, JPEG ou PNG · 4 Mo maximum · seules les 5 premières pages d’un PDF sont analysées. Le fichier complet est transmis à Mistral par le serveur Copiveo : retirez les identités et les pages inutiles avant l’envoi. L’OCR manuscrit reste à relire.</p>
      <p>L’offre gratuite éventuelle et ses quotas dépendent du compte Mistral configuré sur ce serveur. Copiveo ne garantit pas un usage gratuit ou illimité. L’original n’est pas ajouté à votre espace ni aux sauvegardes de Copiveo.</p>
      {loading ? <p role="status">Vérification de la disponibilité de l’OCR distant…</p> : availabilityError ? <p role="note">{availabilityError} L’extraction locale reste disponible.</p>
        : !metadata?.enabled ? <p role="note">{metadata?.availabilityReason === 'quota_blocked' ? 'L’accès Mistral OCR est bloqué par un refus de quota du fournisseur lors des essais. Aucun nouvel envoi distant n’est autorisé pour le moment ; votre texte et votre fichier restent sur cet appareil.' : 'L’OCR Mistral n’est pas activé sur ce serveur. Son accès doit être vérifié avant utilisation.'} L’extraction locale reste disponible.</p>
        : !session?.authenticated || !session?.csrfToken ? <p role="note">Connectez-vous à votre compte enseignant pour utiliser l’OCR Mistral. L’extraction locale reste disponible.</p>
        : !ownerMatches ? <p role="note">La session ne correspond pas à cet espace. Rouvrez le bon compte avant l’envoi.</p>
        : metadata?.provider !== 'mistral' ? <p role="note">La configuration Mistral OCR est indisponible. L’extraction locale reste disponible.</p>
        : <p>Service disponible : Mistral OCR{metadata.model ? ` (${metadata.model})` : ''}.</p>}
      <button type="button" disabled={loading || busy} onClick={() => setRefresh(value => value + 1)}>Vérifier la disponibilité</button>
      <label className="studio-field">Fichier pour Mistral OCR<input ref={fileInput} type="file" accept="application/pdf,image/jpeg,image/png" onChange={choose} disabled={busy || !ready} /></label>
      {file && <p>{file.name} · {(file.size / 1024 / 1024).toFixed(2)} Mo</p>}
      <label className="studio-checkbox"><input type="checkbox" checked={consent} disabled={busy || !ready || !file} onChange={event => setConsent(event.target.checked)} />J’ai retiré les identités et données sensibles du fichier, vérifié le cadre autorisé par mon établissement et j’autorise l’envoi de cette copie à Mistral pour en extraire le texte.</label>
      <div className="studio-actions"><button type="button" className="studio-primary" onClick={extract} disabled={!ready || !file || !consent || busy}>Extraire avec Mistral OCR</button>{busy && <button type="button" onClick={() => cancel('Extraction annulée. Aucun résultat tardif ne remplacera votre texte. Un fichier déjà envoyé peut encore être traité par Mistral.')}>Annuler l’extraction distante</button>}</div>
    </details>
    <label className="studio-field">Texte de la copie de l’élève<textarea rows={8} maxLength={MAX_CHARACTERS} value={text} onChange={event => updateText(event.target.value, source)} placeholder="Collez, saisissez ou importez le travail de l’élève, puis relisez-le." /></label>
    <p>{text.length.toLocaleString('fr-FR')} / 20 000 caractères · {record.studentCopyConfirmed && text.trim() ? 'Texte confirmé après relecture.' : 'Texte à relire et à confirmer.'}</p>
    <button type="button" disabled={busy || !text.trim() || text.length > MAX_CHARACTERS || record.studentCopyConfirmed === true} onClick={() => { onChange({ studentCopy: text, studentCopySource: source, studentCopyConfirmed: true }); setError(''); setStatus('Texte de la copie confirmé après relecture.'); }}>Confirmer le texte relu</button>
    <p>Seul le texte est conservé avec cette correction. L’import et la confirmation ne déclenchent aucune notation automatique.</p>
    <p role="status" aria-live="polite">{status}</p>{error && <p role="alert">{error}</p>}
  </section>;
}
